<template>
  <div class="page-shell">
    <p
      class="mobile-page-title font-helvetica-700 font-bold text-gray-dark lg:text-[40px] lg:uppercase"
    >
      Заказы
    </p>
    <OrdersNavBar />

    <div class="flex flex-col gap-4">
      <section
        v-if="createdOrdersNotice"
        role="status"
        class="flex w-full flex-col gap-3 rounded-[20px] border border-orange/25 bg-warning-soft px-5 py-4 text-gray-dark"
      >
        <div class="flex flex-col gap-1">
          <p class="font-helvetica-700 text-[18px] font-bold">
            {{ createdOrdersTitle }}
          </p>
          <p class="font-helvetica-400 text-[15px] leading-[21px]">
            По выбранным товарам было создано несколько заказов. Проверьте их
            отдельно в списке ниже.
          </p>
        </div>

        <div v-if="createdOrderIds.length" class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="orderId in createdOrderIds"
            :key="orderId"
            :to="`/orders/${encodeURIComponent(orderId)}`"
            class="rounded-full bg-selected-filter px-3 py-1 text-[13px] font-helvetica-700 text-orange"
          >
            {{ orderId }}
          </NuxtLink>
        </div>
      </section>

      <StatusFilter
        :status="status"
        :status-options="statusOptions"
        :organization="organization"
        :organization-options="organizationOptions"
        :counterpart="counterpart"
        :counterpart-options="counterpartOptions"
        :author="author"
        :author-options="authorOptions"
        @update:status="status = $event"
        @update:organization="organization = $event"
        @update:counterpart="counterpart = $event"
        @update:author="author = $event"
      />

      <OrdersTable :orders="orders" :loading="loading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import type { LocationQueryValue } from "vue-router";
import OrdersNavBar from "~/components/orders/OrdersNavBar.vue";
import StatusFilter from "~/components/orders/StatusFilter.vue";
import OrdersTable from "~/components/orders/OrdersTable.vue";
import { useNuxtApp, useRoute } from "#imports";

usePageTitle("Актуальные заказы");

type Option = { label: string; value: string };

const statusOptions = ref<Option[]>([{ label: "Не выбрано", value: "" }]);
const organizationOptions = ref<Option[]>([{ label: "Не выбрано", value: "" }]);
const counterpartOptions = ref<Option[]>([{ label: "Не выбрано", value: "" }]);
const authorOptions = ref<Option[]>([{ label: "Не выбрано", value: "" }]);

const sortOptions: Option[] = [
  { label: "Сначала недорогие", value: "cheap-first" },
  { label: "Сначала дорогие", value: "expensive-first" },
  { label: "По наименованию (от А до Я)", value: "name-asc" },
  { label: "По наименованию (от Я до А)", value: "name-desc" },
];

const route = useRoute();
const getQueryValue = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
) =>
  Array.isArray(value)
    ? typeof value[0] === "string"
      ? value[0]
      : ""
    : typeof value === "string"
      ? value
      : "";
const getQueryValues = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
) => {
  const values = Array.isArray(value) ? value : [value];

  return values
    .flatMap((item) => (typeof item === "string" ? item.split(",") : []))
    .map((item) => item.trim())
    .filter(Boolean);
};

const getOrderCountWord = (count: number) => {
  const modulo100 = count % 100;
  const modulo10 = count % 10;

  if (modulo100 >= 11 && modulo100 <= 14) return "заказов";
  if (modulo10 === 1) return "заказ";
  if (modulo10 >= 2 && modulo10 <= 4) return "заказа";

  return "заказов";
};

const status = ref(getQueryValue(route.query.status));
const organization = ref("");
const counterpart = ref("");
const author = ref("");
const sortValue = ref<string>(sortOptions[0]?.value ?? "");
const dateRange = ref<{ from: string | null; to: string | null }>({
  from: null,
  to: null,
});

type OrderRow = {
  code: string;
  date: string;
  status: string;
  sum: string;
  organization: string;
  contractor: string;
  deliveryStatus?: string;
  deliveryMethod?: string;
  guid?: string;
};

const orders = ref<OrderRow[]>([]);
const loading = ref(true);
const filtersLoading = ref(false);
let ordersRequestId = 0;

const createdOrderIds = computed(() =>
  Array.from(new Set(getQueryValues(route.query.createdOrders))),
);
const createdOrdersCount = computed(() => {
  const parsedCount = Number(getQueryValue(route.query.createdOrdersCount));

  return Number.isFinite(parsedCount) && parsedCount > 0
    ? Math.floor(parsedCount)
    : createdOrderIds.value.length;
});
const createdOrdersNotice = computed(() => createdOrdersCount.value > 1);
const createdOrdersTitle = computed(
  () =>
    `Создано ${createdOrdersCount.value} ${getOrderCountWord(createdOrdersCount.value)}`,
);

const DELIVERY_STATUS_CONCURRENCY = 4;

const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp.$useOneCApi as (
  action: string,
  payload?: Record<string, any>,
) => Promise<any>;

const mapOptions = (items: string[]) => [
  { label: "Не выбрано", value: "" },
  ...items.map((v) => ({ label: v, value: v })),
];
const normalizeText = (value: unknown) => String(value ?? "").trim();

const resolveDeliveryStatus = (deliveryData: any) =>
  normalizeText(deliveryData?.["Состояние"]) ||
  normalizeText(deliveryData?.title) ||
  normalizeText(deliveryData?.["title"]);

const loadDeliveryStatusByGuid = async (guid: string) => {
  const response = await $useOneCApi("getOrdersDelivery", { guid });
  const data = response?.data ?? response;

  if (!data || typeof data !== "object") {
    return "";
  }

  return resolveDeliveryStatus(data);
};

const fetchDeliveryStatuses = async (
  requestId: number,
  baseOrders: OrderRow[],
) => {
  const queue = baseOrders.filter(
    (order) =>
      order.guid &&
      normalizeText(order.deliveryMethod).toLowerCase() === "доставка",
  );

  if (!queue.length) return;

  const workers = Array.from(
    { length: Math.min(DELIVERY_STATUS_CONCURRENCY, queue.length) },
    async () => {
      while (queue.length) {
        const nextOrder = queue.shift();
        if (!nextOrder?.guid) continue;

        try {
          const deliveryStatus = await loadDeliveryStatusByGuid(nextOrder.guid);
          if (requestId !== ordersRequestId) {
            return;
          }

          const targetOrder = orders.value.find(
            (order) => order.guid === nextOrder.guid,
          );
          if (targetOrder) {
            targetOrder.deliveryStatus = deliveryStatus || "—";
          }
        } catch (error) {
          if (requestId !== ordersRequestId) {
            return;
          }

          const targetOrder = orders.value.find(
            (order) => order.guid === nextOrder.guid,
          );
          if (targetOrder) {
            targetOrder.deliveryStatus = "—";
          }
          console.warn(
            `Не удалось загрузить статус доставки заказа ${nextOrder.guid}`,
            error,
          );
        }
      }
    },
  );

  await Promise.allSettled(workers);
};

const fetchFilters = async () => {
  filtersLoading.value = true;
  try {
    const res = await $useOneCApi("getOrderListFilter");
    statusOptions.value = mapOptions(
      Array.isArray(res?.["Состояния"]) ? res["Состояния"] : [],
    );
    organizationOptions.value = mapOptions(
      Array.isArray(res?.["Организации"]) ? res["Организации"] : [],
    );
    counterpartOptions.value = mapOptions(
      Array.isArray(res?.["Контрагенты"]) ? res["Контрагенты"] : [],
    );
    authorOptions.value = mapOptions(
      Array.isArray(res?.["Авторы"]) ? res["Авторы"] : [],
    );
  } catch (error) {
    console.error("Не удалось загрузить фильтры заказов", error);
    statusOptions.value = mapOptions([]);
    organizationOptions.value = mapOptions([]);
    counterpartOptions.value = mapOptions([]);
    authorOptions.value = mapOptions([]);
  } finally {
    filtersLoading.value = false;
  }
};

const fetchOrders = async () => {
  const requestId = ++ordersRequestId;
  loading.value = true;
  try {
    const res = await $useOneCApi("getOrderList", {
      status: status.value,
      company: organization.value,
      client: counterpart.value,
      user: author.value,
    });
    const list = Array.isArray(res?.["Заказы"]) ? res["Заказы"] : [];
    const mappedOrders: OrderRow[] = list.map((item: any) => ({
      code: String(item?.Номер ?? "—"),
      date: String(item?.Дата ?? ""),
      status: String(item?.Состояние ?? ""),
      sum: String(item?.Сумма ?? ""),
      organization: String(item?.Организация ?? ""),
      contractor: String(item?.Контрагент ?? ""),
      deliveryStatus: "",
      deliveryMethod: String(item?.СпособДоставки ?? ""),
      guid: item?.guid ? String(item.guid) : undefined,
    }));
    if (requestId !== ordersRequestId) {
      return;
    }

    orders.value = mappedOrders;
    setTimeout(() => {
      if (requestId !== ordersRequestId) return;
      void fetchDeliveryStatuses(requestId, mappedOrders);
    }, 0);
  } catch (error) {
    console.error("Не удалось загрузить заказы", error);
    if (requestId === ordersRequestId) {
      orders.value = [];
    }
  } finally {
    if (requestId === ordersRequestId) {
      loading.value = false;
    }
  }
};

onMounted(async () => {
  await fetchFilters();
  await fetchOrders();
});

watch(
  () => route.query.status,
  (value) => {
    status.value = getQueryValue(value);
  },
);

watch([status, organization, counterpart, author], () => {
  fetchOrders();
});
</script>
